import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  fetchEntitySchema,
  selectEntitySchema,
  selectEntitySchemaLoading,
} from '@/store/slices/entitySchemaSlice'
import { entitySchemaApi } from '@/api/entitySchemaApi'

/**
 * The five entities that drive ~90% of list-page traffic. Mount the preloader
 * once after sign-in so the first list-page render finds its schema warm.
 */
export const DEFAULT_PRELOAD_ENTITIES = [
  'task',
  'reservation',
  'listing',
  'review',
  'intent',
] as const

export interface SchemaPreloaderProps {
  entities?: readonly string[]
}

/**
 * Side-effect-only component. Renders nothing.
 *
 * Behavior:
 *   - On mount, dispatches `fetchEntitySchema` for each entity. Skips entities
 *     that are currently in-flight to avoid duplicate concurrent requests, but
 *     ALWAYS revalidates cached entries — this is how new deploys propagate
 *     fresh schema_version to clients whose cache has stale data.
 *   - The slice's `fulfilled` reducer overwrites the cache wholesale, so a
 *     bumped schema_version naturally replaces the old entry.
 *   - Individual fetch failures DO NOT throw — each entity is independent, and
 *     the slice's `rejected` reducer records the error per-entity.
 *
 * Place this once after the user is authenticated (e.g. inside the
 * authenticated layout). The component is gated by login: each mount = one
 * round of revalidation. Cheap (≤ N small responses), and the only way users
 * see post-deploy schema changes without a hard refresh.
 */
export function SchemaPreloader({
  entities = DEFAULT_PRELOAD_ENTITIES,
}: SchemaPreloaderProps): null {
  const dispatch = useAppDispatch()

  // Snapshot the loading state once per render so the effect can skip
  // in-flight requests without subscribing to state-shape changes.
  const cacheState = useAppSelector((s) => (s as any).entitySchema?.cache ?? {})

  useEffect(() => {
    for (const entityType of entities) {
      const inFlight = cacheState[entityType]?.loading === true
      if (inFlight) continue

      // No "hasSchema" short-circuit — we revalidate every mount so a new
      // schema_version from the backend propagates to clients.
      void dispatch(
        fetchEntitySchema({
          entityType,
          apiCall: () => entitySchemaApi.getEntitySchema(entityType),
        }),
      )
    }
    // Intentionally do NOT subscribe to cacheState changes — the preloader is
    // a one-shot dispatcher per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, dispatch])

  return null
}

// Silence "unused" warning when consumers only import the component (selectors
// are imported indirectly through the slice). Re-export the loading selector
// so tests/storybook can introspect preload progress without digging in.
export { selectEntitySchema, selectEntitySchemaLoading }
